import {max} from 'lodash';
import {Duration} from 'luxon';

import {isDevelopment, isProduction} from '../../../helpers/environment/envHelpers';
import {isDefined} from '../../../helpers/typescript/typeHelpers';
import {StashItems, StashKey, StashValue} from '../../../models/api/stash/stashModel';
import {ActivationPopoversEnum} from '../../../models/api/stash/values/activationPopovers';
import {StashBooleanKeys} from '../../../models/api/stash/values/booleanValue';
import {makeEnumTypeGuard} from '../../../models/api/types/enumTypes';
import {buildAnnouncementDismissId} from '../../announcements/announcementsHelpers';
import {
  AnnouncementId,
  AnnouncementIdsEnum,
  RUserSurveyAnnouncementId,
} from '../../announcements/announcementsTypes';
import {CommonAppParametricSelector, CommonAppSelector} from '../../commonAppSelectorTypes';
import {CommonAppState} from '../../commonAppState';
import {createCommonSelector} from '../../helpers/commonReselectHelpers';
import {getCompanyUrl} from '../company/commonCompanySelectors';
import {getUserId} from '../teammates/commonTeammatesSelectors';
import {buildStashLocalStorageKey, buildStashUrl} from './stashHelpers';

/*
 * General selectors.
 */

export function getStashLocalStorageKey(state: CommonAppState): string {
  const userId = getUserId(state);
  return buildStashLocalStorageKey(userId);
}

export function getStashUrl(state: CommonAppState): string {
  const companyUrl = getCompanyUrl(state);
  const userId = getUserId(state);
  return buildStashUrl(companyUrl, userId);
}

export function getIsStashInitialized(state: CommonAppState): boolean {
  return state.data.stash.isInitialized;
}

export function getStashItems(state: CommonAppState): StashItems {
  return state.data.stash.items;
}

export function getStashValue<K extends StashKey>(state: CommonAppState, key: K): StashValue<K> {
  return getStashItems(state)[key].value;
}

/** Creates a non-parametric selector for a stash value. */
export function makeGetStashValue<K extends StashKey>(
  key: K,
): CommonAppSelector<CommonAppState, StashValue<K>> {
  return (state) => getStashValue(state, key);
}

export function makeGetBooleanStashValue<K extends StashBooleanKeys>(
  key: K,
): CommonAppSelector<CommonAppState, boolean> {
  return (state) => getStashValue(state, key).isEnabled;
}

/*
 * Specific selectors.
 */

export const getEmojiUsage = makeGetStashValue('emojiUsage');

type IsAnnouncementThrottledSelector = CommonAppParametricSelector<
  CommonAppState,
  {now: number; id: AnnouncementId | undefined},
  boolean
>;

const minTimeBetweenAnnouncements = Duration.fromObject({days: 3}).toMillis();
// Smaller threshold for inbox redesign reminders. Remove this after redesign is on for everyone.
const minTimeBetweenRedesignAnnouncements = Duration.fromObject({days: 1}).toMillis();

const inboxRedesignAnnouncements: ReadonlyArray<AnnouncementId> = [
  AnnouncementIdsEnum.INBOX_REDESIGN_REMINDER_1,
  AnnouncementIdsEnum.INBOX_REDESIGN_REMINDER_2,
  AnnouncementIdsEnum.INBOX_REDESIGN_REMINDER_3,
  AnnouncementIdsEnum.INBOX_REDESIGN_REMINDER_4,
  AnnouncementIdsEnum.INBOX_REDESIGN_REMINDER_5,
];

const announcementThrottledExceptions: ReadonlyArray<AnnouncementId> = [
  AnnouncementIdsEnum.INBOX_REDESIGN_FORCE_ENABLE,
  AnnouncementIdsEnum.SLACK_CHANNEL_USER_AUTH_ANNOUNCEMENT,
  AnnouncementIdsEnum.INBOX_IMPROVEMENTS_LAUNCH,
  AnnouncementIdsEnum.FRONT_UNWRAPPED,
];

export const getLastAnnouncementSeenAt = makeGetStashValue('lastFlashSeenAt');
export const getIsAnnouncementThrottled: IsAnnouncementThrottledSelector = (state, {now, id}) => {
  const permanentlyDismissedIdsOverrides = getPermanentlyDismissedIdsOverrides(state);
  const overrideValue =
    id && permanentlyDismissedIdsOverrides.permanentlyDismissedIds[buildAnnouncementDismissId(id)];
  const hasOverrideThrottle = !isProduction() && overrideValue === false;

  const lastAnnouncementSeenAt = getLastAnnouncementSeenAt(state);
  const isInboxRedesignAnnouncement = id && inboxRedesignAnnouncements.includes(id);

  if (
    hasOverrideThrottle ||
    (id && announcementThrottledExceptions.includes(id)) ||
    RUserSurveyAnnouncementId.is(id)
  ) {
    return false;
  }

  return (
    now - lastAnnouncementSeenAt <
    (isInboxRedesignAnnouncement ? minTimeBetweenRedesignAnnouncements : minTimeBetweenAnnouncements)
  );
};

export const getPermanentlyDismissedIds = makeGetStashValue('permanentlyDismissedIds');

export function makeGetIsPermanentlyDismissedId(
  id: string | undefined,
): CommonAppSelector<CommonAppState, boolean> {
  if (id === undefined) {
    return () => false;
  }

  return createCommonSelector(
    [getPermanentlyDismissedIds, getPermanentlyDismissedIdsOverrides],
    (permanentlyDismissedIds, permanentlyDismissedIdsOverrides) => {
      const overrideValue = permanentlyDismissedIdsOverrides.permanentlyDismissedIds[id];

      if (!isProduction() && isDefined(overrideValue) && overrideValue === false) {
        return false;
      }

      return permanentlyDismissedIds.contains(id);
    },
  );
}

export const getMobilePrefetcherConfig = makeGetStashValue('mobilePrefetcher');

export const getDismissedProductUpdates = makeGetStashValue('dismissedProductUpdates');

export const getDismissedSuggestions = makeGetStashValue('dismissedSuggestions');

export const getActivationPopovers = createCommonSelector(
  [makeGetStashValue('activationPopovers')],
  (activationPopovers) => activationPopovers.popovers,
);

const getIsTriggeredSuggestionPopoverOpen = createCommonSelector(
  [getActivationPopovers],
  (activationPopovers) => {
    // This is written a little strangely, but the idea is to use TS to prompt engineers
    // to consider this case whenever adding a new activation popover. Because an activation
    // popover isn't necessarily a triggered suggestion popover.
    const triggeredSuggestionPopoverMap: Record<ActivationPopoversEnum, boolean> = {
      [ActivationPopoversEnum.LIVE_DASHBOARD_SUGGESTION]: true,
      [ActivationPopoversEnum.ENABLE_AI_COPILOT_SUGGESTION]: true,
      [ActivationPopoversEnum.CONFIGURE_AI_WORKSPACE_SETTINGS_SUGGESTION]: true,
      [ActivationPopoversEnum.CONFIGURE_AI_COPILOT_KNOWLEDGE_BASE_SUGGESTION]: true,
      [ActivationPopoversEnum.DISCARD_SUGGESTED_REPLY_KNOWLEDGE_BASE_SUGGESTION]: true,
      [ActivationPopoversEnum.ENABLE_AI_SLACK_INBOXES_SUGGESTION]: true,
    };

    const isActivationPopover = makeEnumTypeGuard(ActivationPopoversEnum);

    const isTriggeredSuggestionPopover = (popoverKey: string) => {
      if (!isActivationPopover(popoverKey)) {
        return false;
      }

      return triggeredSuggestionPopoverMap[popoverKey];
    };

    return Object.keys(activationPopovers).some(isTriggeredSuggestionPopover);
  },
);

// Determine if we should tigger a new suggestion based on the time the last suggestion was shown.
export const getSuggestionWasTriggeredRecently = createCommonSelector(
  [getIsTriggeredSuggestionPopoverOpen, getDismissedSuggestions, getDismissedProductUpdates],
  (isTriggeredSuggestionPopoverOpen, dismissedTriggeredSuggestions, dismissedProductUpdates) =>
    (nowMilliseconds: number) => {
      const cooldownPeriodMilliseconds = isDevelopment()
        ? Duration.fromObject({second: 30}).toMillis()
        : Duration.fromObject({day: 1}).toMillis();

      const latestUpdateTimestamp = Math.max(
        ...[...Object.values(dismissedTriggeredSuggestions), ...Object.values(dismissedProductUpdates)],
      );

      const wasTriggeredRecently = latestUpdateTimestamp > nowMilliseconds - cooldownPeriodMilliseconds;

      return isTriggeredSuggestionPopoverOpen || wasTriggeredRecently;
    },
);

const getSynopsisFeedbackDismissedAt = makeGetStashValue('synopsisFeedbackDismissedAt');

const synopsisFeedbackTemporarilyDismissDuration = Duration.fromObject({hours: 24}).toMillis();
export const synopsisFeedbackPermanentlyDismissedCount = 2;

export function getIsSynopsisFeedbackDismissed(state: CommonAppState, now: number): boolean {
  const dismissedAt = getSynopsisFeedbackDismissedAt(state).toArray();

  // Permanently dismissed?
  const dismissedCount = dismissedAt.length;
  if (dismissedCount >= synopsisFeedbackPermanentlyDismissedCount) {
    return true;
  }

  // Temporarily dismissed?
  const lastDismissedAt = max(dismissedAt) ?? 0;
  return now - lastDismissedAt < synopsisFeedbackTemporarilyDismissDuration;
}

const getSmartQAReviewFeedbackDismissedAt = makeGetStashValue('smartQaReviewFeedbackDismissedAt');

const getSmartQAReviewFeedbackDismissedForConversation = makeGetStashValue(
  'smartQaReviewFeedbackDismissedForConversation',
);

const SMART_QA_REVIEW_FEEDBACK_DISMISSED_MAX_COUNT = 3;
const SMART_QA_REVIEW_FEEDBACK_DISMISSED_DURATION_MS = Duration.fromObject({hours: 24}).toMillis();

export function getIsSmartQAReviewFeedbackDismissed(
  state: CommonAppState,
  now: number,
  conversationId: number,
): boolean {
  const dismissedForConversation = getSmartQAReviewFeedbackDismissedForConversation(state);
  if (dismissedForConversation.has(conversationId)) {
    return true;
  }

  const dismissedAtArray = getSmartQAReviewFeedbackDismissedAt(state).toArray();
  if (dismissedAtArray.length >= SMART_QA_REVIEW_FEEDBACK_DISMISSED_MAX_COUNT) {
    return true;
  }

  const lastDismissedAt = max(dismissedAtArray) ?? 0;
  return now - lastDismissedAt < SMART_QA_REVIEW_FEEDBACK_DISMISSED_DURATION_MS;
}

export const getIsSmartQaAiTermsBannerDismissed = makeGetBooleanStashValue('isSmartQaAiTermsBannerDismissed');
export const getHasVisitedSmartQAAnalytics = makeGetBooleanStashValue('hasVisitedSmartQAAnalytics');

export const getShouldShowAbsoluteDatesOnDesktopValue = makeGetBooleanStashValue('shouldShowAbsoluteDates');

export const getShouldShowAbsoluteDatesOnMobileValue = makeGetBooleanStashValue(
  'shouldShowAbsoluteDatesOnMobile',
);

export const getMailingListDismissedSenders = makeGetStashValue('mailingListDismissedSenders');

export const getStashFeatureFlagOverrides = createCommonSelector(
  [getStashItems],
  (stash) => stash.featureFlagOverrides.value.flags,
);

export const getShouldShowInternalToolbar = makeGetBooleanStashValue('shouldShowInternalToolbar');

export const getHasInboxRedesignPreferenceEnabled = makeGetBooleanStashValue(
  'hasInboxRedesignPreferenceEnabled',
);

export const getHasMobileInboxRedesignPreferenceEnabled = makeGetBooleanStashValue(
  'hasMobileInboxRedesignPreferenceEnabled',
);

export const getIsP3MetadataExpanded = makeGetBooleanStashValue('isP3MetadataExpanded');
export const getHasInboxImprovementsToggleStashEnabled = makeGetBooleanStashValue(
  'hasInboxImprovementsToggleEnabled',
);

// AI compose assistant custom prompt
export const getAiComposeAssistantCustomPrompt = makeGetStashValue('aiComposeAssistantCustomPrompt');

export const getPermanentlyDismissedIdsOverrides = makeGetStashValue('permanentlyDismissedIdsOverrides');

export const getUserSurveySubmissionHistory = makeGetStashValue('userSurveySubmissionHistory');

export const getAiTermsAndPrivacyAcceptance = makeGetStashValue('aiTermsAndPrivacyAcceptance');
